import React from 'react';
import { Check, ArrowRight, Users, Clock, Presentation, Wifi, Zap, PenTool } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function BetterOnlineWorkshops() {
  const {
    t
  } = useLanguage();
  const benefits = t('betterOnlineWorkshops.benefits') as string[];
  const sessions = t('betterOnlineWorkshops.sessions') as Array<{
    title: string;
    desc: string;
    topics: string[];
    isHomework?: boolean;
  }>;
  const forWhomItems = t('betterOnlineWorkshops.forWhomItems') as string[];
  const forWhomIcons = [<Presentation className="text-purple-600 shrink-0" size={20} data-id="element-400" />, <Zap className="text-purple-600 shrink-0" size={20} data-id="element-401" />, <Users className="text-purple-600 shrink-0" size={20} data-id="element-402" />, <PenTool className="text-purple-600 shrink-0" size={20} data-id="element-403" />];
  return <PageTransition data-id="element-404">
      <div className="pt-12 pb-24" data-id="element-405">
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-406">
          <DecorativeShape type="circle" color="bg-purple-100" className="top-0 right-0 w-[400px] h-[400px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-407" />
          <DecorativeShape type="blob" color="bg-fuchsia-100" className="bottom-0 left-0 w-64 h-64 -translate-x-1/4 translate-y-1/4 -z-10" data-id="element-408" />
          <Animate className="text-center max-w-4xl mx-auto" data-id="element-409">
            <span className="inline-block px-4 py-2 bg-purple-50 text-purple-600 font-medium rounded-full text-sm mb-6" data-id="element-410">
              {t('betterOnlineWorkshops.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-411">
              {t('betterOnlineWorkshops.title')} <br data-id="element-412" />
              <span className="text-purple-600" data-id="element-413">
                {t('betterOnlineWorkshops.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-414">
              {t('betterOnlineWorkshops.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm font-medium text-gray-500 mb-10" data-id="element-415">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-416">
                <Clock size={18} className="text-purple-600" data-id="element-417" />
                <span data-id="element-418">{t('betterOnlineWorkshops.duration')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-419">
                <Presentation size={18} className="text-purple-600" data-id="element-420" />
                <span data-id="element-421">{t('betterOnlineWorkshops.audience')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-422">
                <Wifi size={18} className="text-purple-600" data-id="element-423" />
                <span data-id="element-424">{t('betterOnlineWorkshops.online')}</span>
              </div>
            </div>
            <Link to="/contact" data-id="element-425">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white shadow-purple-200" size="lg" rightIcon={<ArrowRight size={20} data-id="element-427" />} data-id="element-426">
                {t('betterOnlineWorkshops.bookCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-428">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-429">
            <div className="lg:col-span-2 space-y-12" data-id="element-430">
              <Animate data-id="element-431">
                <h2 className="text-3xl font-bold mb-6 text-charcoal" data-id="element-432">
                  {t('betterOnlineWorkshops.aboutTitle')}
                </h2>
                <div className="prose prose-lg text-gray-600" data-id="element-433">
                  <p className="mb-4" data-id="element-434">{t('betterOnlineWorkshops.aboutP1')}</p>
                  <p data-id="element-435">{t('betterOnlineWorkshops.aboutP2')}</p>
                </div>
              </Animate>
              <Animate delay={0.1} data-id="element-436">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-437">
                  {t('betterOnlineWorkshops.learnTitle')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-438">
                  {benefits.map((benefit: string, index: number) => <div key={index} className="flex items-start gap-3" data-id="element-439">
                      <div className="mt-1 p-1 rounded-full bg-purple-50 text-purple-600 flex-shrink-0" data-id="element-440">
                        <Check size={14} strokeWidth={3} data-id="element-441" />
                      </div>
                      <span className="text-gray-700" data-id="element-442">{benefit}</span>
                    </div>)}
                </div>
              </Animate>
              <Animate delay={0.15} data-id="element-443">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-444">
                  {t('betterOnlineWorkshops.structureTitle')}
                </h3>
                <div className="space-y-6" data-id="element-445">
                  {sessions.map((session, index) => <Card key={index} className={`p-6 border-l-4 ${session.isHomework ? 'border-yellow-400 bg-yellow-50' : 'border-purple-600 bg-white'}`} data-id="element-446">
                      <div className="flex items-start justify-between mb-2" data-id="element-447">
                        <h4 className="text-xl font-bold text-charcoal" data-id="element-448">
                          {session.title}
                        </h4>
                        {session.isHomework && <span className="bg-yellow-200 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full" data-id="element-449">
                            {t('betterOnlineWorkshops.homework')}
                          </span>}
                      </div>
                      <p className="text-gray-600 mb-4" data-id="element-450">{session.desc}</p>
                      <div className={`${session.isHomework ? 'bg-white' : 'bg-purple-50'} p-4 rounded-xl`} data-id="element-451">
                        <p className={`text-sm font-bold ${session.isHomework ? 'text-yellow-800' : 'text-purple-800'} mb-2`} data-id="element-452">
                          {t('betterOnlineWorkshops.topicsLabel')}
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-453">
                          {session.topics.map((topic: string, i: number) => <li key={i} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-454">
                              <div className={`w-1.5 h-1.5 rounded-full ${session.isHomework ? 'bg-yellow-500' : 'bg-purple-600'}`} data-id="element-455" />
                              {topic}
                            </li>)}
                        </ul>
                      </div>
                    </Card>)}
                </div>
              </Animate>
            </div>
            <div className="lg:col-span-1" data-id="element-456">
              <div className="sticky top-24 space-y-8" data-id="element-457">
                <Card className="p-8 bg-white shadow-xl border-t-4 border-purple-600" data-id="element-458">
                  <h3 className="text-xl font-bold mb-6 text-charcoal" data-id="element-459">
                    {t('betterOnlineWorkshops.forWhom')}
                  </h3>
                  <ul className="space-y-4 mb-8" data-id="element-460">
                    {forWhomItems.map((item: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-461">
                        {forWhomIcons[i]}
                        <span className="text-gray-600" data-id="element-462">{item}</span>
                      </li>)}
                  </ul>
                  <Link to="/contact" className="block" data-id="element-463">
                    <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white shadow-purple-200" rightIcon={<ArrowRight size={18} data-id="element-465" />} data-id="element-464">
                      {t('betterOnlineWorkshops.inquiry')}
                    </Button>
                  </Link>
                </Card>
                <div className="bg-purple-50 rounded-3xl p-8 text-center" data-id="element-466">
                  <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md mb-4 text-purple-600" data-id="element-467">
                    <Users size={32} data-id="element-468" />
                  </div>
                  <h4 className="font-bold text-lg mb-2" data-id="element-469">
                    {t('betterOnlineWorkshops.groupTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm mb-4" data-id="element-470">
                    {t('betterOnlineWorkshops.groupDesc')}
                  </p>
                  <Link to="/contact" className="text-purple-600 font-bold hover:underline" data-id="element-471">
                    {t('betterOnlineWorkshops.contactLink')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-472">
          <Animate data-id="element-473">
            <Card className="p-12 bg-gradient-to-br from-purple-50 to-fuchsia-50 border-0" data-id="element-474">
              <h2 className="text-3xl font-bold mb-4" data-id="element-475">
                {t('betterOnlineWorkshops.ctaTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-476">
                {t('betterOnlineWorkshops.ctaDesc')}
              </p>
              <Link to="/contact" data-id="element-477">
                <Button className="bg-purple-600 hover:bg-purple-700 text-white shadow-purple-200" rightIcon={<ArrowRight size={18} data-id="element-479" />} data-id="element-478">
                  {t('betterOnlineWorkshops.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}